import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Calendar, MapPin, Star, FileText, RefreshCw, Clock,
  MessageSquareWarning, User as UserIcon, ArrowLeft, XCircle, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import JobPhotoDocumentation from "@/components/JobPhotoDocumentation";
import TripTracker from "@/components/TripTracker";

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  matched: "bg-blue-50 text-blue-700 border-blue-200",
  active: "bg-teal-50 text-teal-700 border-teal-200",
  in_progress: "bg-teal-50 text-teal-700 border-teal-200",
  completed: "bg-slate-100 text-slate-700 border-slate-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  cancelled_by_cleaner: "bg-orange-50 text-orange-700 border-orange-200",
};

const UPCOMING_STATUSES = ["pending", "matched"];
const IN_PROGRESS_STATUSES = ["active", "in_progress"];
const COMPLETED_STATUSES = ["completed"];
const CANCELLED_STATUSES = ["cancelled", "cancelled_by_cleaner"];

function AddressBlock({ j }) {
  return (
    <div className="text-xs text-slate-500 flex items-start gap-1 mt-1">
      <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
      <div>
        {j.street_address ? (
          <>
            <div>{j.street_address}{j.unit ? `, ${j.unit}` : ""}</div>
            <div>{[j.city, j.state, j.postal_code].filter(Boolean).join(", ")}{j.country ? ` · ${j.country}` : ""}</div>
          </>
        ) : (<span>{j.postal_code}, {j.country}</span>)}
      </div>
    </div>
  );
}

export default function MyBookings() {
  const nav = useNavigate();
  const { currency, user } = useApp();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescheduleJob, setRescheduleJob] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [cancelJob, setCancelJob] = useState(null);
  const [cancelPreview, setCancelPreview] = useState(null);
  const [cancelReason, setCancelReason] = useState("no_longer_needed");
  const [cancelNotes, setCancelNotes] = useState("");
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [photosJob, setPhotosJob] = useState(null);
  const [issueJob, setIssueJob] = useState(null);
  const [issueType, setIssueType] = useState("cleaning_quality");
  const [issueDesc, setIssueDesc] = useState("");
  const [myComplaints, setMyComplaints] = useState([]);

  const loadComplaints = async () => {
    try {
      const { data } = await api.get("/complaints/mine");
      setMyComplaints(data || []);
    } catch (e) { /* ignore */ }
  };

  const submitComplaint = async () => {
    if (!issueJob) return;
    if (!issueDesc || issueDesc.trim().length < 10) {
      toast.error("Please describe what happened (10+ characters).");
      return;
    }
    try {
      await api.post("/complaints", {
        job_id: issueJob.id,
        issue_type: issueType,
        description: issueDesc.trim(),
      });
      toast.success("Complaint filed. CLEANBAZ will review and follow up.");
      setIssueJob(null); setIssueDesc(""); setIssueType("cleaning_quality");
      loadComplaints();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to file complaint");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/jobs/mine");
      setJobs(data);
    } catch { toast.error("Failed to load bookings"); }
    setLoading(false);
  };
  useEffect(() => { load(); loadComplaints(); }, []);

  const upcoming = jobs.filter(j => UPCOMING_STATUSES.includes(j.status));
  const inProgress = jobs.filter(j => IN_PROGRESS_STATUSES.includes(j.status));
  const completed = jobs.filter(j => COMPLETED_STATUSES.includes(j.status));
  const cancelled = jobs.filter(j => CANCELLED_STATUSES.includes(j.status));

  const doReschedule = async () => {
    if (!newDate) return;
    try {
      const { data } = await api.post(`/jobs/${rescheduleJob.id}/reschedule`, { scheduled_at: new Date(newDate).toISOString() });
      if (data.late_fee > 0) toast.warning(`Late fee applied: $${data.late_fee.toFixed(2)}`);
      else toast.success("Rescheduled");
      setRescheduleJob(null); setNewDate(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openCancelDialog = async (j) => {
    setCancelJob(j);
    setCancelReason("no_longer_needed");
    setCancelNotes("");
    setCancelPreview(null);
    try {
      const { data } = await api.get(`/jobs/${j.id}/cancellation-preview`);
      setCancelPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load cancellation details");
    }
  };

  const doCancel = async () => {
    if (!cancelJob || cancelSubmitting) return;
    setCancelSubmitting(true);
    try {
      await api.post(`/jobs/${cancelJob.id}/cancel-by-client`, {
        reason: cancelReason,
        notes: cancelNotes.trim(),
      });
      toast.success("Booking cancelled");
      setCancelJob(null);
      setCancelPreview(null);
      setCancelNotes("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel booking");
    } finally {
      setCancelSubmitting(false);
    }
  };

  const renderJobCard = (j, { bucket }) => (
    <Card key={j.id} className="border-slate-200 hover:shadow-md transition-all" data-testid={`booking-card-${j.id}`}>
      {j.last_cancellation && j.status === "pending" && (
        <div className="border-b border-amber-200 bg-amber-50 px-6 py-3 text-xs text-amber-900 flex items-start gap-2" data-testid={`reassign-banner-${j.id}`}>
          <RefreshCw className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-700" />
          <div>
            <div className="font-semibold text-amber-900">Your cleaner had to cancel — CLEANBAZ is reassigning</div>
            <div className="text-amber-800/80 mt-0.5">
              You are not being re-charged. Your payment stays protected — held by CLEANBAZ — while we find a nearby cleaner right now.
            </div>
          </div>
        </div>
      )}
      <CardContent className="p-6 grid md:grid-cols-5 gap-4 items-center">
        <div className="md:col-span-2">
          <Badge className={`${STATUS_STYLES[j.status]} border font-mono text-[10px] uppercase`} data-testid={`booking-status-${j.id}`}>{j.status.replace(/_/g, " ")}</Badge>
          <div className="mt-2 font-display font-bold text-lg text-slate-900">
            {j.bedrooms} bed · {j.bathrooms} bath{j.deep_clean && " · Deep"}
          </div>
          <AddressBlock j={j} />
          {j.cleaner_name && (
            <div className="mt-2 text-xs text-slate-600 flex items-center gap-1">
              <UserIcon className="w-3 h-3" />
              <span>Cleaner: <span className="text-slate-900 font-medium">{j.cleaner_name}</span></span>
            </div>
          )}
        </div>
        <div>
          <div className="text-xs font-mono uppercase text-slate-500">Scheduled</div>
          <div className="text-sm text-slate-900 mt-1 flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {new Date(j.scheduled_at).toLocaleString()}
          </div>
          {bucket === "completed" && j.review && (
            <div className="mt-2 flex items-center gap-1" data-testid={`booking-rating-${j.id}`}>
              {Array.from({ length: j.review.rating }).map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="text-xs font-mono uppercase text-slate-500">Total</div>
          <div className="font-display font-bold text-2xl text-teal-600 mt-1">{formatPrice(j.total, currency)}</div>
          {j.payment_status && (
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mt-1">{j.payment_status}</div>
          )}
        </div>
        <div className="flex flex-col gap-2 md:items-end">
          {bucket === "upcoming" && (
            <>
              {j.cleaner_id && (
                <TripTracker jobId={j.id} />
              )}
              <Button size="sm" variant="outline" onClick={() => { setRescheduleJob(j); setNewDate(j.scheduled_at.slice(0, 16)); }} data-testid={`booking-reschedule-${j.id}`}>
                <RefreshCw className="w-3.5 h-3.5 mr-1" />Reschedule
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => openCancelDialog(j)}
                data-testid={`booking-cancel-${j.id}`}
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <XCircle className="w-3.5 h-3.5 mr-1" />Cancel Booking
              </Button>
            </>
          )}
          {bucket === "cancelled" && j.cancellation_reason && (
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500" data-testid={`booking-cancel-reason-${j.id}`}>
              Reason: {j.cancellation_reason.replace(/_/g, " ")}
            </div>
          )}
          {bucket === "in_progress" && (
            <Button size="sm" variant="outline" onClick={() => nav("/dashboard")} data-testid={`booking-track-${j.id}`} className="border-teal-300 text-teal-700 hover:bg-teal-50">
              Live tracking
            </Button>
          )}
          {bucket === "completed" && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPhotosJob(j)}
                data-testid={`booking-view-photos-${j.id}`}
                className="border-teal-300 text-teal-700 hover:bg-teal-50"
              >
                <FileText className="w-3.5 h-3.5 mr-1" />View Photos
              </Button>
              {(() => {
                const openComp = myComplaints.find((c) => c.job_id === j.id && c.status === "under_review");
                const anyComp = myComplaints.find((c) => c.job_id === j.id);
                return openComp ? (
                  <span data-testid={`booking-complaint-status-${j.id}`} className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">Complaint · under review</span>
                ) : anyComp ? (
                  <span data-testid={`booking-complaint-status-${j.id}`} className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">Complaint · {anyComp.status.replace(/_/g, " ")}</span>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setIssueJob(j); setIssueType("cleaning_quality"); setIssueDesc(""); }}
                    data-testid={`booking-report-issue-${j.id}`}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    <MessageSquareWarning className="w-3.5 h-3.5 mr-1" />Report an Issue
                  </Button>
                );
              })()}
              {j.booking_type === "business" && j.payment_status === "paid" ? (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${j.id}/invoice.pdf?auth=${localStorage.getItem("bb_token")}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`booking-invoice-${j.id}`}
                >
                  <Button variant="ghost" size="sm"><FileText className="w-3.5 h-3.5 mr-1" />Invoice PDF</Button>
                </a>
              ) : (
                <Button variant="ghost" size="sm" data-testid={`booking-receipt-${j.id}`}><FileText className="w-3.5 h-3.5 mr-1" />Receipt</Button>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const EmptyState = ({ label }) => (
    <Card><CardContent className="p-10 text-center text-slate-500" data-testid="bookings-empty">{label}</CardContent></Card>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <button onClick={() => nav("/dashboard")} className="text-xs font-mono uppercase tracking-widest text-teal-600 hover:text-teal-700 inline-flex items-center gap-1" data-testid="back-to-dashboard">
            <ArrowLeft className="w-3 h-3" />Back to Dashboard
          </button>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 mt-2">My Bookings</h1>
          <p className="text-sm text-slate-500 mt-1">All your CLEANBAZ bookings, organized.</p>
        </div>
        <Button onClick={() => nav("/services")} data-testid="bookings-new-btn" className="bg-slate-900 hover:bg-slate-800 text-white">Book new cleaning</Button>
      </div>

      <Tabs defaultValue="upcoming">
        <TabsList data-testid="bookings-tabs">
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">Upcoming ({upcoming.length})</TabsTrigger>
          <TabsTrigger value="in_progress" data-testid="tab-in-progress">In Progress ({inProgress.length})</TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">Completed ({completed.length})</TabsTrigger>
          <TabsTrigger value="cancelled" data-testid="tab-cancelled">Cancelled ({cancelled.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="mt-6">
          {loading ? <div className="text-slate-500">Loading…</div> : upcoming.length === 0 ? (
            <EmptyState label="No upcoming bookings." />
          ) : (
            <div className="grid gap-4">{upcoming.map((j) => renderJobCard(j, { bucket: "upcoming" }))}</div>
          )}
        </TabsContent>

        <TabsContent value="in_progress" className="mt-6">
          {loading ? <div className="text-slate-500">Loading…</div> : inProgress.length === 0 ? (
            <EmptyState label="No bookings are currently in progress." />
          ) : (
            <div className="grid gap-4">{inProgress.map((j) => renderJobCard(j, { bucket: "in_progress" }))}</div>
          )}
        </TabsContent>

        <TabsContent value="completed" className="mt-6">
          {loading ? <div className="text-slate-500">Loading…</div> : completed.length === 0 ? (
            <EmptyState label="No completed bookings yet." />
          ) : (
            <div className="grid gap-4">{completed.map((j) => renderJobCard(j, { bucket: "completed" }))}</div>
          )}
        </TabsContent>

        <TabsContent value="cancelled" className="mt-6">
          {loading ? <div className="text-slate-500">Loading…</div> : cancelled.length === 0 ? (
            <EmptyState label="No cancelled bookings." />
          ) : (
            <div className="grid gap-4">{cancelled.map((j) => renderJobCard(j, { bucket: "cancelled" }))}</div>
          )}
        </TabsContent>
      </Tabs>

      {/* Reschedule Dialog */}
      <Dialog open={!!rescheduleJob} onOpenChange={(o) => !o && setRescheduleJob(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">One-click reschedule</DialogTitle></DialogHeader>
          <div className="space-y-3 py-4">
            <Label>New date & time</Label>
            <Input type="datetime-local" value={newDate} onChange={(e) => setNewDate(e.target.value)} data-testid="reschedule-input" />
            <div className="text-xs text-amber-700 bg-amber-50 p-3 rounded-lg flex items-start gap-2">
              <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Rescheduling under 24 hours incurs a 15% late fee per policy.</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleJob(null)}>Cancel</Button>
            <Button onClick={doReschedule} data-testid="reschedule-confirm" className="bg-teal-600 text-white">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Dialog */}
      <Dialog open={!!cancelJob} onOpenChange={(o) => !o && !cancelSubmitting && (setCancelJob(null), setCancelPreview(null))}>
        <DialogContent className="bg-white max-w-lg" data-testid="cancel-booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" />
              Cancel booking
            </DialogTitle>
          </DialogHeader>
          {cancelJob && (
            <div className="space-y-4 py-2">
              {/* Booking summary */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs space-y-1" data-testid="cancel-booking-summary">
                <div className="flex justify-between"><span className="text-slate-500 uppercase tracking-widest text-[10px] font-mono">Service</span><span className="text-slate-900 font-medium">{cancelJob.booking_type || "cleaning"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500 uppercase tracking-widest text-[10px] font-mono">Exact service</span><span className="text-slate-900 font-medium">{cancelJob.service_key || cancelJob.car_service || cancelJob.pet_service || cancelJob.outdoor_service || cancelJob.convenience_service || `${cancelJob.bedrooms} bed · ${cancelJob.bathrooms} bath`}</span></div>
                <div className="flex justify-between"><span className="text-slate-500 uppercase tracking-widest text-[10px] font-mono">Scheduled</span><span className="text-slate-900 font-medium">{cancelJob.scheduled_at ? new Date(cancelJob.scheduled_at).toLocaleString() : "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500 uppercase tracking-widest text-[10px] font-mono">Booking ID</span><span className="text-slate-900 font-mono">{cancelJob.id?.slice(0, 8)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500 uppercase tracking-widest text-[10px] font-mono">Total</span><span className="text-slate-900 font-medium">{formatPrice(cancelJob.total, currency)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500 uppercase tracking-widest text-[10px] font-mono">Payment</span><span className="text-slate-900 font-medium uppercase">{cancelJob.payment_status || "unpaid"}</span></div>
              </div>

              {/* Financial impact */}
              {cancelPreview ? (
                <div className={`rounded-lg border p-3 text-xs ${cancelPreview.payment_status === "paid" && !cancelPreview.within_grace ? "border-amber-300 bg-amber-50" : "border-teal-200 bg-teal-50"}`} data-testid="cancel-financial-impact">
                  {cancelPreview.payment_status !== "paid" ? (
                    <div className="flex items-start gap-2 text-slate-800">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-500" />
                      <span>This booking is <b>unpaid</b>. No refund will be issued. Cancelling releases the slot.</span>
                    </div>
                  ) : cancelPreview.within_grace ? (
                    <div className="space-y-1">
                      <div className="flex justify-between"><span className="text-slate-600">Original payment</span><span className="font-medium text-slate-900">{formatPrice(cancelPreview.total, currency)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-600">Cancellation fee</span><span className="font-medium text-teal-700">{formatPrice(0, currency)}</span></div>
                      <div className="flex justify-between border-t border-teal-200 pt-1 mt-1"><span className="text-slate-800 font-semibold">Refund to your card</span><span className="font-bold text-teal-700" data-testid="cancel-refund-amount">{formatPrice(cancelPreview.refund_amount, currency)}</span></div>
                      <div className="text-[11px] text-teal-800 mt-1">Cancelling more than {cancelPreview.cancellation_grace_hours} hours in advance — full refund applies.</div>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div className="flex justify-between"><span className="text-slate-600">Original payment</span><span className="font-medium text-slate-900">{formatPrice(cancelPreview.total, currency)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-600">Cancellation fee ({cancelPreview.late_cancel_fee_pct}%)</span><span className="font-medium text-amber-800" data-testid="cancel-fee-amount">- {formatPrice(cancelPreview.cancellation_fee, currency)}</span></div>
                      <div className="flex justify-between border-t border-amber-300 pt-1 mt-1"><span className="text-slate-800 font-semibold">Refund to your card</span><span className="font-bold text-amber-800" data-testid="cancel-refund-amount">{formatPrice(cancelPreview.refund_amount, currency)}</span></div>
                      <div className="text-[11px] text-amber-800 mt-1">Late cancellation — within the {cancelPreview.cancellation_grace_hours}-hour policy window ({cancelPreview.hours_until.toFixed(1)}h before start).</div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-slate-500">Loading cancellation details…</div>
              )}

              {/* Confirmation copy */}
              <div className="text-sm text-slate-800 font-medium">Are you sure you want to cancel this booking?</div>

              {/* Reason */}
              <div>
                <Label>Cancellation reason <span className="text-red-600">*</span></Label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  data-testid="cancel-reason-select"
                  className="mt-1 w-full h-10 border rounded-md px-3 text-sm bg-white"
                >
                  <option value="no_longer_needed">No longer need the service</option>
                  <option value="schedule_changed">Schedule changed</option>
                  <option value="found_another_provider">Found another provider</option>
                  <option value="cost">Cost</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <Label>Additional details (optional)</Label>
                <textarea
                  rows={3}
                  value={cancelNotes}
                  onChange={(e) => setCancelNotes(e.target.value)}
                  data-testid="cancel-notes"
                  placeholder="Anything else we should know?"
                  className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setCancelJob(null); setCancelPreview(null); }} disabled={cancelSubmitting} data-testid="cancel-dialog-back">
              Keep booking
            </Button>
            <Button
              onClick={doCancel}
              disabled={cancelSubmitting || !cancelPreview}
              data-testid="cancel-dialog-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {cancelSubmitting ? "Cancelling…" : "Yes, cancel booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photos Dialog */}
      <Dialog open={!!photosJob} onOpenChange={(o) => !o && setPhotosJob(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Job Photos · Booking {photosJob?.id?.slice(0, 8)}</DialogTitle>
          </DialogHeader>
          {photosJob && (
            <Tabs defaultValue="before" className="mt-2">
              <TabsList>
                <TabsTrigger value="before" data-testid="cust-view-before">Before</TabsTrigger>
                <TabsTrigger value="after" data-testid="cust-view-after">After</TabsTrigger>
              </TabsList>
              <TabsContent value="before" className="mt-4">
                <JobPhotoDocumentation jobId={photosJob.id} phase="before" mode="view" />
              </TabsContent>
              <TabsContent value="after" className="mt-4">
                <JobPhotoDocumentation jobId={photosJob.id} phase="after" mode="view" />
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPhotosJob(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report an Issue Dialog */}
      <Dialog open={!!issueJob} onOpenChange={(o) => !o && setIssueJob(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="report-issue-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <MessageSquareWarning className="w-5 h-5 text-amber-600" />
              Report an issue
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
              CLEANBAZ will review the evidence and applicable policy. Filing a complaint does <b>not</b> automatically trigger a refund or payout adjustment — CLEANBAZ makes a determination based on the full record. See the <a className="underline" href="/legal/dispute-policy" target="_blank" rel="noopener noreferrer">Dispute &amp; Damage Policy</a>.
            </div>
            <div>
              <Label>Issue type</Label>
              <select
                value={issueType}
                onChange={(e) => setIssueType(e.target.value)}
                data-testid="issue-type"
                className="mt-1 w-full h-10 border rounded-md px-3 text-sm"
              >
                <option value="damage">Damage</option>
                <option value="missing_or_damaged_property">Missing / damaged property</option>
                <option value="cleaning_quality">Cleaning quality</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label>What happened?</Label>
              <textarea
                rows={5}
                value={issueDesc}
                onChange={(e) => setIssueDesc(e.target.value)}
                data-testid="issue-desc"
                placeholder="Describe what you observed, when, and any relevant details."
                className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
              />
              <div className="text-[11px] text-slate-500 mt-1">Booking {issueJob?.id?.slice(0, 8)} · Scheduled {issueJob?.scheduled_at ? new Date(issueJob.scheduled_at).toLocaleDateString() : ""}</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIssueJob(null)}>Cancel</Button>
            <Button onClick={submitComplaint} data-testid="issue-submit" className="bg-amber-600 hover:bg-amber-700 text-white">Submit complaint</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
